import bwipJs from 'bwip-js';
import { findLastBarcodeSequenceByType } from '../services/inventoryService'; // Import the new service function
import { ItemType, InventoryItem, Currency } from '../types/item'; // Added Currency
import { BarcodeTemplate } from '../types/ui'; // Added BarcodeTemplate

/**
 * Returns standardized CSS for barcode printing
 * This ensures consistent styling across all barcode print modals
 */
export const getStandardBarcodePrintStyles = (): string => {
  return `
    @page {
      size: 72mm 10mm;
      margin: 0;
    }
    body {
      margin: 0;
      padding: 0;
      width: 100%;
      height: auto;
      page-break-inside: avoid;
    }
    #print-content {
      display: block;
    }
    /* Remove or comment out unused .print-container */
    /* .print-container {
      width: 72mm;
      height: 10mm;
      display: flex;
      box-sizing: border-box;
      page-break-inside: avoid;
      page-break-after: avoid;
      margin-bottom: 0;
    } */
    /* Add styles for the actual container class used */
    .barcode-label-container {
      width: 72mm;
      height: 10mm;
      display: flex;
      box-sizing: border-box;
      page-break-inside: avoid;
      margin-bottom: 0;
    }
    .barcode-side {
      width: 20mm;
      height: 10mm;
      box-sizing: border-box;
      padding: 0; /* Explicitly set padding to 0 */
      font-size: 7pt;
      line-height: 1.1;
      font-family: Arial, sans-serif;
      text-align: center;
      overflow: hidden; /* Prevent overflow */
    }
    .barcode-side:first-child {
      border-right: 1px dashed #ccc;
    }
    .barcode-side:nth-child(2) {
      border-right: 1px dashed #ccc;
      padding-left: 0.75mm; /* Add padding to move content away from the fold */
    }
    /* Add margin to SVG elements in second barcode-side */
    .barcode-side:nth-child(2) svg {
      margin-left: 0.75mm;
    }
    .tail {
      width: 32mm;
      height: 10mm;
      box-sizing: border-box;
    }
    /* Styles for the Data Matrix container itself */
    .data-matrix-container {
      width: 9mm;
      height: 9mm;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0.5mm auto; /* Center vertically with 0.5mm margin, horizontally with auto */
    }
    .data-matrix-container svg {
      width: 100%;
      height: 100%;
      display: block;
    }
  `;
};

/**
 * Generate a unique sequential barcode string.
 * Format: TGS-<TypePrefix>-<SequenceNumber>
 * Example: TGS-S-0001, TGS-W-0123
 * 
 * Fetches the last used sequence number for the given type and increments it.
 */
export const generateDataMatrixBarcode = async (
  itemType: ItemType
): Promise<string> => {
  const typePrefix = itemType === ItemType.SILVER ? 'S' : 'W';
  const storePrefix = 'TGS';

  try {
    // Fetch the last sequence number used for this item type
    const lastSequence = await findLastBarcodeSequenceByType(itemType);
    const nextSequence = lastSequence + 1;

    // Format the sequence number with 4 leading zeros
    const sequenceString = nextSequence.toString().padStart(4, '0');

    // Combine parts
    const newBarcode = `${storePrefix}-${typePrefix}-${sequenceString}`;
    
    // Note: This assumes findLastBarcodeSequenceByType provides the correct *absolute* last sequence.
    // Additional checks for uniqueness within a batch might still be needed in the calling component.
    return newBarcode;

  } catch (error) {
    console.error("Error generating sequential barcode:", error);
    // Fallback to a timestamp-based unique ID in case of error
    // This fallback format should also be considered valid by validateDataMatrix or handled separately
    return `${storePrefix}-${typePrefix}-ERR-${Date.now()}`;
  }
};

/**
 * Validate barcode string based on the format: TGS-<TypePrefix>-<SequenceNumber>
 * or the error fallback format: TGS-<TypePrefix>-ERR-<Timestamp>
 */
export const validateDataMatrix = (barcode: string): boolean => {
  if (!barcode || typeof barcode !== 'string') {
    return false;
  }

  const parts = barcode.split('-');
  
  // Check for base structure (at least 3 parts: TGS, Type, Sequence/ERR)
  if (parts.length < 3) {
    return false;
  }
  
  const [store, type, part3] = parts;

  // Check store prefix
  if (store !== 'TGS') {
    return false;
  }

  // Check type prefix
  if (type !== 'S' && type !== 'W') {
    return false;
  }

  // Check for standard format: TGS-X-NNNN
  if (parts.length === 3 && part3.length === 4 && /^\d{4}$/.test(part3)) {
    return true;
  }

  // Check for error fallback format: TGS-X-ERR-Timestamp
  if (parts.length === 4 && part3 === 'ERR' && !isNaN(parseInt(parts[3], 10))) {
    return true;
  }

  return false; // Does not match expected formats
};

/**
 * Generate a Data Matrix SVG string using bwip-js.
 * 
 * @param text The text to encode.
 * @param size Target size in mm (width/height). This is now primarily used for calculating scale if needed, but symbolwidth/height take precedence for matrix size.
 * @returns A promise resolving to the SVG string.
 */
export const generateDataMatrixSVG = async (text: string, size: number): Promise<string> => {
  // console.log(`[barcodeUtils] generateDataMatrixSVG called with text: "${text}", size: ${size}`);
  
  // size parameter is less relevant now, but keep calculation for potential future use or logging
  const sizeInPoints = size * 2.83; 
  // console.log(`[barcodeUtils] Original requested size in points: ${sizeInPoints} (Using symbol dimensions and scale instead)`);

  const options = {
    bcid: 'datamatrix',   // Barcode type
    text: text,          // Text to encode
    symbolheight: 16,    // Explicit symbol height in modules (e.g., 16x16 matrix)
    symbolwidth: 16,     // Explicit symbol width in modules
    scale: 3,            // Scale the symbol *after* setting its module dimensions. Adjust as needed for desired physical size.
    includetext: false,  // Do not include human-readable text
    textxalign: 'center' as 'center', // Explicitly cast to the expected literal type
    backgroundcolor: 'ffffff', // White background
    paddingwidth: 1,    // Set padding to 1 module width for quiet zone
    paddingheight: 1,   // Set padding to 1 module width for quiet zone
  };

  try {
    // console.log('[barcodeUtils] Calling bwipJs.toSVG with options:', options);
    // Call toSVG without a callback; it should return a Promise
    const svg = await bwipJs.toSVG(options);
    
    // Check if svg is actually a string and has content
    if (typeof svg === 'string' && svg.length > 0) {
      // console.log('[barcodeUtils] bwipJs.toSVG Promise resolved successfully. SVG length:', svg.length);
      return svg;
    } else {
      console.error('[barcodeUtils] bwipJs.toSVG Promise resolved with invalid SVG:', svg);
      throw new Error('bwipJs.toSVG returned invalid SVG content');
    }

  } catch (err) {
    console.error('[barcodeUtils] Error occurred during bwipJs.toSVG call:', err);
    // Ensure we throw an actual Error object
    const error = err instanceof Error ? err : new Error(String(err) || 'Unknown error generating SVG');
    throw error; // Re-throw the error to be caught by the caller
  }
}; 

/**
 * Generates the complete HTML string for a single printable barcode label.
 *
 * @param barcode The barcode string.
 * @param frontSideSvg The pre-generated SVG string for the front side (Side A) of the label.
 * @param template The BarcodeTemplate to use for styling and content.
 * @param item Optional. The InventoryItem details, required for templates C and D.
 * @param currentExchangeRate Optional. The current USD to TRY exchange rate, required for template D if item currency is TRY.
 * @returns A string containing the HTML for one barcode label.
 */
export const generatePrintableLabelHtml = (
  barcode: string,
  frontSideSvg: string,
  template: BarcodeTemplate,
  item?: InventoryItem,
  currentExchangeRate?: number,
): string => {
  let sideAContentHtml = `<div class="data-matrix-container">${frontSideSvg}</div>`; // Default for Side A (original template A, B, C, D, E)
  let sideBContentHtml = '';

  if (template === BarcodeTemplate.A) {
    // Template A: NEW LAYOUT
    // Side A: Data Matrix (left) + Barcode ID (right)
    sideAContentHtml = `
      <div style="display: flex; align-items: center; justify-content: flex-start; width: 100%; height: 100%; box-sizing: border-box; padding-left: 0.5mm;">
        <div class="data-matrix-container" style="flex-shrink: 0; margin-right: 1mm; margin-left:0; margin-top:auto; margin-bottom:auto;">
          ${frontSideSvg}
        </div>
        <div style="font-size: 6pt;
                    font-family: Arial, sans-serif;
                    text-align: left;
                    line-height: 1;
                    overflow: hidden; /* Avoid text spilling */
                    /* white-space: nowrap; /* Prevent wrapping if too long, might get cut */
                    /* text-overflow: ellipsis; */ /* Show ellipsis if cut */
                    display: flex;
                    align-items: center;
                    height: 100%;
                    flex-grow: 1; /* Allow text to take remaining space */
                    padding-right: 0.5mm;">
          ${barcode}
        </div>
      </div>
    `;

    // Side B: USD Price (similar to Template D)
    if (item) {
      let priceText = '';
      if (item.sellCurrency === Currency.USD) {
        priceText = `$${item.sellPerUnit.toFixed(2)}`;
      } else if (item.sellCurrency === Currency.TRY && currentExchangeRate && currentExchangeRate > 0) {
        const usdPrice = item.sellPerUnit / currentExchangeRate;
        priceText = `$${usdPrice.toFixed(2)}`;
      } else if (item.sellCurrency === Currency.TRY) {
        priceText = 'USD Kur Bekleniyor'; // More specific if rate is missing
      } else {
        priceText = 'USD Fiyat Belirsiz'; // Other currencies or missing data
      }
      sideBContentHtml = `<div style="font-size: 11pt; font-weight: bold; text-align: center; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; line-height: 1; padding-left: 0.75mm;">${priceText}</div>`;
    } else {
      // Fallback for Template A Side B if item is not provided (e.g., batch mode)
      sideBContentHtml = `<div style="font-size: 7pt; text-align: center; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; padding-left: 0.75mm;">USD Fiyat Yok</div>`;
    }

  } else if (template === BarcodeTemplate.B) {
    // Template B: Blank back side
    sideBContentHtml = '';
  } else if (template === BarcodeTemplate.E) {
    // Template E: Barcode ID string on the back side
    sideBContentHtml = `<div style="font-size: 9pt; line-height: 1.1; font-family: Arial, sans-serif; text-align: center; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; padding-left: 0.75mm;">${barcode}</div>`;
  } else if (item) {
    // Templates C and D require item information
    const currencySymbol = item.sellCurrency === Currency.TRY ? '₺' : '$';
    if (template === BarcodeTemplate.C) {
      // Template C: Item details on the back side
      let detailsHtml = '';
      if (item.type === ItemType.SILVER) {
        detailsHtml = `
          <div>Ağırlık: ${item.weight.toFixed(2)}gr</div>
          <div>Fiyat/gr: ${item.pricePerGram.toFixed(2)}${currencySymbol}</div>
          <div>Satış: ${item.sellPerUnit.toFixed(2)}${currencySymbol}</div>
        `;
      } else if (item.type === ItemType.WATCH) {
        detailsHtml = `
          ${item.brand ? `<div>Marka: ${item.brand}</div>` : ''}
          <div>Satış: ${item.sellPerUnit.toFixed(2)}${currencySymbol}</div>
        `;
      }
      sideBContentHtml = `<div style="font-size: 7pt; line-height: 1.1; font-family: Arial, sans-serif; text-align: center; width: 100%; height: 100%; display: flex; flex-direction: column; justify-content: center; padding-left: 0.75mm;">${detailsHtml}</div>`;
    } else if (template === BarcodeTemplate.D) {
      // Template D: USD price on the back side
      let priceText = '';
      if (item.sellCurrency === Currency.USD) {
        priceText = `$${item.sellPerUnit.toFixed(2)}`;
      } else if (item.sellCurrency === Currency.TRY && currentExchangeRate && currentExchangeRate > 0) {
        const usdPrice = item.sellPerUnit / currentExchangeRate;
        priceText = `$${usdPrice.toFixed(2)}`;
      } else if (item.sellCurrency === Currency.TRY) {
        priceText = 'USD Fiyat Bekleniyor';
      } else {
        priceText = 'USD Fiyat Belirlenmedi';
      }
      sideBContentHtml = `<div style="font-size: 11pt; font-weight: bold; text-align: center; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; line-height: 1; padding-left: 0.75mm;">${priceText}</div>`;
    }
  } else if (template === BarcodeTemplate.C || template === BarcodeTemplate.D) {
    // Fallback for Templates C or D if item is not provided
    sideBContentHtml = `<div style="font-size: 7pt; text-align: center; padding-left: 0.75mm;">Ürün bilgisi yok</div>`;
  }

  // For templates B, C, D, E, Side A is still just the Data Matrix centered.
  // For the new Template A, sideAContentHtml is already set.
  // For other templates, if sideAContentHtml was not specifically set (i.e. old template A behavior is removed),
  // ensure it defaults to the standard DM rendering.
  // The initial `sideAContentHtml` declaration handles this for B, C, D, E.

  return `
    <div class="barcode-label-container template-${template}">
      <div class="barcode-side">
        ${sideAContentHtml}
      </div>
      <div class="barcode-side">
        ${sideBContentHtml}
      </div>
      <div class="tail"></div>
    </div>
  `;
};